import { Component, OnDestroy, ViewChild, ElementRef, OnInit, ViewContainerRef } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { AddressFormComponent } from '@shared/address-form/address-form.component';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import * as Types from '@store/modals';
import { fromSelectors } from '@store/selectors';
import { fromActions } from '@store/actions';
import { CurrentComponentService } from '@services/current-component';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { WindowRefService } from '@services/window-ref-service';
import { AddressBookI } from '@store/modals';
import { Table } from '@cureskin/api-client';
import { EventLoggerService } from '@services/event-logger-service';
import { UserProfileEditComponent } from '../../user-profile/user-profile-edit/user-profile-edit.component';
import { AppConfig } from 'client/app/app.config';
import { ShiprocketService } from '@services/shiprocket-service';
import { CheckoutService } from '@services/checkout-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { AddressPageLocationFlow } from '@services/experiment/experiment.service.types';
import { extractPincode } from 'client/helper/geo-address-helper/geo-address-helper';
import {
  AddressAutofillService, AutofillFocus, DetectFailure, DetectionState,
} from '../services/address-autofill.service';
import { AddressAutofillHandoff, AddressAutofillHandoffService } from '../services/address-autofill-handoff.service';
import { AddressLocationSheetComponent, LocationSheetResult } from '../address-location-sheet/address-location-sheet.component';
import {
  RemainingDetailsSheetComponent, RemainingDetailsSheetData, RemainingDetailsSheetResult,
} from '../remaining-details-sheet/remaining-details-sheet.component';


const PINCODE_LENGTH = 6;

export enum AddressPopUpType {
  InvalidPincode = 'invalidPincode',
}

const NOT_ABLE_TO_DETECT_LOCATION: string = $localize`:@@notAbleToDetectLocation:Not able to detect location`;
const LOCATION_PERMISSION_DENIED: string = 'Location permission denied. Kindly enable permission in your browser settings.';
const ADDRESS_PAGE_ENTRY: string = 'address_page';
const AUTOFILL_SHEET_CONFIG: { backdropClass: string } = {
  backdropClass: 'address-autofill-sheet-backdrop',
};

@Component({
  selector: 'user-address-edit',
  templateUrl: './user-address-edit.html',
  styleUrls: ['./user-address-edit.scss'],
  standalone: false,
  providers: [AddressAutofillService],
})
export class UserAddressEditComponent implements OnDestroy, OnInit {
  address: any;
  addressForm: AddressBookI;
  user: any;
  popUpModal: { open: boolean, type?: AddressPopUpType } = { open: false };
  readonly AddressPopUpType: typeof AddressPopUpType = AddressPopUpType;
  saveInProcess$: Observable<boolean> = this.store.select(fromSelectors.selectAddressSaveInProcess);
  checkoutInProgress$: Observable<boolean> = this.store.select(fromSelectors.selectCheckoutProgress);
  isEdit: boolean;
  loading: boolean = true;
  inProcess: boolean = false;
  isLocatingAddress: boolean;
  subscriptions: Subscription[] = [];
  experiments: Array<any> = [];
  autoLocationPermissionEnabled: boolean = false;
  addressPageLocationFlow: AddressPageLocationFlow | null = null;
  showLocationCtaOnReviewOrder: boolean = false;
  isAutofillFlow: boolean = false;
  private pendingHandoff: AddressAutofillHandoff | null = null;
  hideLocation: boolean = false;
  private autofilledValues: { localityDetails: string, zipCode: number } | null = null;
  shouldEnableUseCurrentLocationButton: boolean = false;
  startTimer:number;
  prefillAddressShiprocketExperiment: boolean = false;
  shiprocketSticky: boolean = true;
  showShiprocketBanner: boolean = true;
  shiprocketAddressExists: boolean = false;
  shiprocketAddressesCount: number = 0;
  shiprocketPhone: string = '';
  private isHandlingShiprocketFlowEditSave: boolean = false;
  shouldShowShiprocketBannerNonSticky: boolean = false;
  shouldShowShiprocketBannerSticky: boolean = false;
  isKeyboardVisible: boolean = false;
  private hasSentBannerImpression: boolean = false;
  private bannerObserver: IntersectionObserver | null = null;
  fromReviewOrderPage: boolean = false;
  fromAddressList: boolean = false;
  fromMenu: boolean = false;
  expectedDeliveryDate: { from?: Date; to?: Date } = {};
  dayDifference: number | undefined;

  @ViewChild(AddressFormComponent) addressFormComponent: AddressFormComponent;
  @ViewChild(UserProfileEditComponent) profileEdit: UserProfileEditComponent;
  @ViewChild('shiprocketBannerNonSticky', { read: ElementRef }) shiprocketBannerNonSticky: ElementRef;
  @ViewChild('shiprocketBannerSticky', { read: ElementRef }) shiprocketBannerSticky: ElementRef;

  constructor(
    private conn: ConnectionService,
    private broadcast: BroadcastService,
    private route: ActivatedRoute,
    private store: Store,
    private currentComponentService: CurrentComponentService,
    private appWebBridge: AppWebBridgeService,
    private readonly bottomSheet: MatBottomSheet,
    private eventLogger: EventLoggerService,
    private window: WindowRefService,
    private readonly appConfig: AppConfig,
    private shiprocketService: ShiprocketService,
    private readonly checkoutService: CheckoutService,
    private readonly location: Location,
    private readonly experimentService: ExperimentService,
    private readonly viewContainerRef: ViewContainerRef,
    readonly autofill: AddressAutofillService,
    private readonly handoff: AddressAutofillHandoffService,
  ) {
    this.currentComponentService.set(this);
  }

  canSaveAddress(): boolean {
    const addressFormComponent = this.addressFormComponent;
    const form = addressFormComponent?.addressForm;
    if (!form) return false;

    const raw: any = form.getRawValue ? form.getRawValue() : {};

    const isNonEmpty = (value: any): boolean =>
      typeof value === 'string' ? value.trim().length > 0 : value !== undefined && value !== null && `${value}`.trim().length > 0;

    const zip: string = `${raw.zipCode ?? ''}`.trim();
    const isZipValid = zip.length === PINCODE_LENGTH && !addressFormComponent.pinCodeError;

    // Required fields (as per UI): name, pincode, city, house/building, locality.
    const requiredFieldsFilled =
      isNonEmpty(raw.contactName)
      && isNonEmpty(raw.buildingDetails)
      && isNonEmpty(raw.localityDetails)
      && isNonEmpty(raw.city)
      && isZipValid;

    // Mobile number is shown/required only in checkout flow for non-internal users.
    const requiresMobileNumber =
      !!addressFormComponent.isCheckout && !addressFormComponent.internalUser;
    const mobileOk = !requiresMobileNumber
      || (isNonEmpty(raw.mobileNumber)
        && `${raw.mobileNumber}`.trim().length === 10
        && form.controls?.mobileNumber?.valid !== false);

    const fieldErrors: any = addressFormComponent.addressFieldError || {};
    const hasCustomFieldErrors =
      !!fieldErrors?.contactName?.contactNameError
      || !!fieldErrors?.city?.cityError
      || !!fieldErrors?.buildingDetails?.buildingDetailsError
      || !!fieldErrors?.localityDetails?.localityDetailsError
      || !!fieldErrors?.landmark?.landmarkError;
    return requiredFieldsFilled
      && mobileOk
      && form.valid
      && !hasCustomFieldErrors
      && !addressFormComponent.pinCodeError;
  }

  async ngOnInit(): Promise<void> {
    this.appWebBridge.setStatusBarColor('white');
    this.startTimer = new Date().getTime();
    await this.setUserExperiments();
    this.hideLocation = !this.shouldEnableUseCurrentLocationButton;
    this.user = this.conn.getActingUser();
    this.fromReviewOrderPage = this.route.snapshot.queryParamMap.has('fromReviewOrder');
    this.fromAddressList = this.route.snapshot.queryParamMap.has('fromAddressList');
    this.fromMenu = this.route.snapshot.queryParamMap.has('fromMenu');
    const addressEditId: string = this.route.snapshot.params.id;
    this.isEdit = !!(addressEditId?.length);
    this.isAutofillFlow = !!this.addressPageLocationFlow && !this.isEdit;
    this.pendingHandoff = !this.isEdit && this.showLocationCtaOnReviewOrder ? this.handoff.consume() : null;
    if (this.isAutofillFlow || this.pendingHandoff) this.prefillAddressShiprocketExperiment = false;
    const isNewAddress: boolean = !this.isEdit;
    // Include explicit new/edit flag in pageOpen payload
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'address-page', isNewAddress }), [
      this.appConfig.Shared.EventTracker.MIXPANEL,
      this.appConfig.Shared.EventTracker.AMPLITUDE,
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    this.appWebBridge.logEventInBranchAndFirebaseFromiOS({
      branch: { name: 'pageOpenAddressPage' },
      firebase: { name: 'pageOpenAddressPage' },
    });
    await this.fetchOrCreateAddress(addressEditId);
    this.subscriptions.push(this.broadcast.on<{ [key: string]: string }>('LOCATION_UPDATE')
      .subscribe(async (latLong: { [key: string]: string }): Promise<void> => {
        if (this.autofill.state() === DetectionState.Detecting) return;
        await this.getDetailedLocationFromCoordinates(latLong);
        this.isLocatingAddress = false;
      }));

    if (this.autoLocationPermissionEnabled) {
      this.getLocationDetails();
    }
    if (!this.isAutofillFlow && !this.pendingHandoff) {
      await this.initShiprocket();
    }

    const time = new Date().getTime() - this.startTimer;
    this.eventLogger.trackEvent('address_screen_loading_time', { timeInMilliSec: time, userId: this.user.id });
    this.loading = false;

    if (this.pendingHandoff) {
      setTimeout((): void => this.applyHandoff(this.pendingHandoff));
    }

    if (this.fromReviewOrderPage) {
      this.initDeliveryDateEstimation();
      setTimeout((): void => {
        this.subscribeToZipCodeChanges();
      });
    }
  }

  async setUserExperiments(): Promise<void> {
    await this.experimentService.experimentsLoaded;
    this.addressPageLocationFlow = this.experimentService.getAddressPageLocationFlow();
    this.showLocationCtaOnReviewOrder = this.experimentService.shouldShowLocationCtaOnReviewOrder();
    this.experiments = await this.conn.findUserActiveExperiments();
    this.experiments.forEach((each: any): void => {
      if (each.key === 'auto_ask_location_permission') this.autoLocationPermissionEnabled = true;
      if (each.key === 'use_current_location_button_enable') this.shouldEnableUseCurrentLocationButton = true;
      if (each.key === 'prefill_address_shiprocket') {
        this.prefillAddressShiprocketExperiment = true;
        this.shiprocketSticky = each?.variant?.sticky !== false;
        this.showShiprocketBanner = each?.variant?.showBanner !== false;
      }
    });
  }

  async checkShipRocketAddressExists(): Promise<void> {
    if (!this.user || !this.shiprocketService.isAcquisitionUser()) {
      this.shiprocketAddressExists = false;
      return;
    }

    try {
      const response = await this.conn.checkIfUserAddressExistsInShipRocket({
        countryCode: '91',
        phone: this.user.get('MobileNumber'),
      });
      this.shiprocketAddressExists = response?.exists ?? false;
      if (this.shiprocketAddressExists) {
        this.eventLogger.cleverTapEvent('shiprocket_address_found', JSON.stringify({ userId: this.user.id }));
      }
    } catch (error) {
      this.shiprocketAddressExists = false;
    }
  }


  selectShiprocketAddress(): void {
    const phone = this.getShiprocketPhone();
    if (!phone) return;
    this.navigateToShiprocketAddressList(phone);
  }

  openShiprocketAddressBottomsheet(): void {
    if (!this.user) return;
    this.shiprocketService.startShiprocketAddressFlow(
      this.bottomSheet,
      this.user.get('MobileNumber'),
      (phone: string): void => this.navigateToShiprocketAddressList(phone),
    );
  }

  showPopup(): void {
    this.popUpModal = {
      open: true,
      type: AddressPopUpType.InvalidPincode,
    };
  }

  async fetchOrCreateAddress(addressId?: string): Promise<void> {
    if (addressId) this.address = await this.conn.fetchAddressById(addressId);
    else {
      this.address = {} as AddressBookI;
    }
    if (!this.address) {
      this.broadcast.broadcast('NOTIFY', { message: 'Address not found.' });
      return;
    }
    const addressForm: Types.AddressBookI = JSON.parse(JSON.stringify(this.address));
    if (!addressId) {
      addressForm.contactName = this.user?.get('PatientName');
    }
    this.addressForm = addressForm;
  }

  async saveAddress(): Promise<void> {
    await this.addressFormComponent.validateForm('parent');
    if (!this.addressFormComponent.addressForm.valid) return;
    if (this.addressFormComponent.pinCodeError) {
      this.showPopup();
      return;
    }
    this.applyAutofillPayload();

    const isShiprocketFlowEdit = this.isShiprocketFlowEdit();
    const isAutofillCheckout = this.isAutofillCheckout;
    const isDirectAdd = this.isDirectAdd;

    this.saveAddressToStore();

    if (isShiprocketFlowEdit) {
      this.handleShiprocketFlowEditSave();
      return;
    }

    if (isAutofillCheckout) {
      this.handleAutofillCheckoutSave();
      return;
    }

    if (isDirectAdd) {
      this.handleDirectAddSave();
      return;
    }

    if (this.fromMenu) {
      this.handleMenuFlowSave();
    }
  }

  // Direct-add (skip-to-payment) fires for any new address typed in a checkout flow
  // (not when editing an existing address from the list — that returns to the list).
  // Gated on order context so a non-checkout entry can never trigger a checkout.
  get isDirectAdd(): boolean {
    return !this.fromAddressList && this.checkoutService.hasOrderContext(this.route.snapshot.queryParams);
  }

  private handleDirectAddSave(): void {
    this.inProcess = true;
    this.saveInProcess$.pipe(
      filter((inProcess: boolean): boolean => !inProcess),
      take(1),
    ).subscribe(async (): Promise<void> => {
      this.replaceUrlWithSavedAddressEdit();
      try {
        await this.checkoutService.initiateRegimenCheckout(
          this.route.snapshot.queryParams,
          this.user,
        );
      } finally {
        this.inProcess = false;
      }
    });
  }

  get isAutofillCheckout(): boolean {
    const isRegimenReorder = this.route.snapshot.queryParams?.isRegimenReorder === 'true';
    return this.autofill.autofilled() && this.checkoutService.hasOrderContext(this.route.snapshot.queryParams) && !isRegimenReorder;
  }

  private applyAutofillPayload(): void {
    if (!this.autofill.autofilled()) return;
    this.handleAutofillLabels();
    this.addressForm.latLong = this.autofill.coordinates;
    this.addressForm.reverseGeocodeAPIResponse = this.autofill.reverseGeocodeResponse;
  }

  private handleAutofillCheckoutSave(): void {
    this.inProcess = true;
    this.saveInProcess$.pipe(
      filter((inProcess: boolean): boolean => !inProcess),
      take(1),
    ).subscribe(async (): Promise<void> => {
      this.replaceUrlWithSavedAddressEdit();
      try {
        await this.conn.navigateToURL(this.autofill.confirmAddressUrl(this.route.snapshot.queryParams));
      } finally {
        this.inProcess = false;
      }
    });
  }

  private handleMenuFlowSave(): void {
    this.inProcess = true;
    this.saveInProcess$.pipe(
      filter((inProcess: boolean): boolean => !inProcess),
      take(1),
    ).subscribe((): void => {
      this.inProcess = false;
      this.conn.navigateToURL('/user/address', false, true);
    });
  }

  private replaceUrlWithSavedAddressEdit(): void {
    this.store.select(fromSelectors.selectDefaultAddress).pipe(take(1)).subscribe((address: AddressBookI): void => {
      if (!address?.objectId) return;
      const query = this.checkoutService.buildQueryString(this.route.snapshot.queryParams);
      this.location.replaceState(`/user/address/${address.objectId}/edit`, query);
    });
  }

  private handleAutofillLabels(): void {
    const controls = this.addressFormComponent?.addressForm?.controls;
    if (!this.autofilledValues || !controls) return;

    const edited = controls.localityDetails.value !== this.autofilledValues.localityDetails
      || `${controls.zipCode.value}` !== `${this.autofilledValues.zipCode}`;
    if (edited) {
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'edited-autofilled-address-from-location' }));
    }
    const label = edited ? 'autofill_location_edited' : 'autofill_location';
    this.addressForm.labels = Array.from(new Set([...(this.addressForm.labels || []), label]));
  }

  private isShiprocketFlowEdit(): boolean {
    const params = this.route.snapshot.queryParams || {};
    return this.isEdit && params.source === this.appConfig.Shared.Shiprocket.Source;
  }

  private saveAddressToStore(): void {
    this.currentComponentService.remove(this);
    this.addressForm.mobileNumber = String(this.addressForm.mobileNumber);
    const setAsDeliveryAddress = this.isDirectAdd || this.isAutofillCheckout;
    this.store.dispatch(fromActions.AddressBookSaveBegin({
      addressId: this.address?.objectId,
      address: this.addressForm as Types.AddressBookI,
      skipNavigationBack: setAsDeliveryAddress || this.fromMenu,
      setAsDeliveryAddress,
    }));
  }

  private handleShiprocketFlowEditSave(): void {
    this.isHandlingShiprocketFlowEditSave = true;
    this.saveInProcess$.pipe(
      filter((inProcess: boolean): boolean => !inProcess),
      take(1),
    ).subscribe(async (): Promise<void> => {
      await this.updateStoreWithSavedAddress();
      this.clearShiprocketData();
      await this.navigateToCheckoutWithParams();
      this.isHandlingShiprocketFlowEditSave = false;
    });
  }

  private async updateStoreWithSavedAddress(): Promise<void> {
    const savedAddress = await this.conn.fetchAddressById(this.address?.objectId);
    if (!savedAddress) return;
    const savedAddressJson = JSON.parse(JSON.stringify(savedAddress));
    this.store.dispatch(fromActions.AddressBookUpdate({
      payload: {
        defaultAddress: savedAddressJson,
      },
    }));
  }

  private async navigateToCheckoutWithParams(): Promise<void> {
    const params = this.route.snapshot.queryParams || {};
    const checkoutUrl = this.checkoutService.buildUrlWithParams('/user/checkout', params);
    await this.conn.navigateToURL(checkoutUrl);
  }

  private clearShiprocketData(): void {
    this.shiprocketService.clearAllAddressData();
    this.shiprocketService.clearLoginToken();
  }

  closePopup(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'no-autofill-pincode' }));
    this.popUpModal = { open: false };
    this.back();
  }

  onClickYes(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'yes-autofill-pincode' }));
    this.closePopup();
    this.getLocationDetails();
  }

  getLocationDetails(): any {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'use-my-current-location' }));
    this.isLocatingAddress = true;
    if (this.appWebBridge.getLocationDetails()) return;
    this.window.nativeWindow.navigator.geolocation.getCurrentPosition((location: any): void => {
      this.getDetailedLocationFromCoordinates({ latitude: location.coords.latitude, longitude: location.coords.longitude });
    }, (): any => {
      this.isLocatingAddress = false;
      this.broadcast.broadcast('NOTIFY', { message: LOCATION_PERMISSION_DENIED });
    });
  }

  async getDetailedLocationFromCoordinates(location: { [key: string]: string }): Promise<void> {
    const data = await this.conn.getDetailedLocationFromCoordinates(location);
    this.isLocatingAddress = false;
    try {
      const pincode = extractPincode(data);
      if (pincode) this.addressFormComponent.addressForm.controls.zipCode.setValue(pincode);
      if (this.address?.objectId) {
        const addressBook = new Table.AddressBook();
        addressBook.id = this.address.objectId;
        addressBook.set('latLong', location);
        (addressBook as unknown as { set: (key: string, value: unknown) => void }).set('reverseGeocodeAPIResponse', data);
        await addressBook.save();
      }
    } catch (err) {
      this.broadcast.broadcast('NOTIFY', { message: 'Error while getting location.' });
    }
  }

  get showRemainingDetailsBottomSheet(): boolean {
    return this.addressPageLocationFlow === AddressPageLocationFlow.BottomSheet;
  }

  get showAutofillLocationCta(): boolean {
    return this.isAutofillFlow && !this.autofill.autofilled();
  }

  get showLegacyLocationCta(): boolean {
    return !this.hideLocation && !this.isAutofillFlow;
  }

  get showAutofilledFormStyle(): boolean {
    return this.isAutofillFlow || this.autofill.autofilled();
  }

  get disclaimerSpacingClass(): string {
    return this.showAutofilledFormStyle ? 'tw-mb-3' : 'tw-mb-0.5';
  }

  get showProceedToPayCta(): boolean {
    return this.isDirectAdd && !this.isAutofillFlow && !this.isAutofillCheckout;
  }

  get showHeaderBar(): boolean {
    return this.fromReviewOrderPage || this.isAutofillFlow;
  }

  get showFillRemainingState(): boolean {
    return this.isAutofillFlow && !this.showRemainingDetailsBottomSheet && this.autofill.autofilled();
  }

  get detectedLocationLine(): string {
    const location = this.autofill.location();
    return [location?.area, location?.city].filter(Boolean).join(', ');
  }

  removeDetectedLocation(): void {
    const controls = this.addressFormComponent?.addressForm?.controls;
    ['localityDetails', 'zipCode', 'city', 'state'].forEach((key: string): void => controls?.[key]?.reset());
    this.addressFormComponent?.setRequiredFieldErrorsVisible(false);
    this.autofilledValues = null;
    this.autofill.clear();
    this.logAutofillEvent('location_removed');
  }

  async startLocationAutofill(): Promise<void> {
    if (this.autofill.state() === DetectionState.Detecting) return;
    this.logAutofillEvent('location_sheet_opened');
    const sheetRef = this.bottomSheet.open(AddressLocationSheetComponent, { ...AUTOFILL_SHEET_CONFIG, viewContainerRef: this.viewContainerRef });
    this.currentComponentService.set(sheetRef.instance);
    const dismissed = firstValueFrom(sheetRef.afterDismissed());
    dismissed.then((): void => this.autofill.cancel());

    const outcome = await this.autofill.detect({
      withDelivery: this.checkoutService.hasOrderContext(this.route.snapshot.queryParams),
      languagePreference: this.user?.get('languagePreference'),
    });
    if (!outcome.detected && outcome.reason !== DetectFailure.Cancelled) {
      sheetRef.dismiss();
      this.notifyDetectFailure(outcome.reason);
    }
    const result = await dismissed;
    this.currentComponentService.remove(sheetRef.instance);
    this.currentComponentService.set(this);
    if (!outcome.detected) return;

    const isEditRequest = result === LocationSheetResult.Edit;
    this.logAutofillEvent(isEditRequest ? 'location_edit_clicked' : 'location_confirmed');
    await this.applyDetectedLocation(isEditRequest ? AutofillFocus.Area : AutofillFocus.Building);
  }

  private async applyDetectedLocation(requestedFocus: AutofillFocus): Promise<void> {
    const location = this.autofill.location();
    const controls = this.addressFormComponent?.addressForm?.controls;
    if (!location || !controls) return;
    if (location.area) controls.localityDetails.setValue(location.area);
    if (location.pincode) controls.zipCode.setValue(location.pincode);
    this.autofilledValues = { localityDetails: controls.localityDetails.value, zipCode: controls.zipCode.value };
    this.autofill.autofilled.set(true);
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'autofilled-address-from-location' }));

    const focus = location.area ? requestedFocus : AutofillFocus.Area;
    if (this.showRemainingDetailsBottomSheet) {
      await this.openRemainingDetailsSheet(focus);
      return;
    }
    this.showFillRemainingDetails(focus);
  }

  private applyHandoff(handoff: AddressAutofillHandoff | null): void {
    const controls = this.addressFormComponent?.addressForm?.controls;
    if (!handoff || !controls) return;
    this.autofill.restore(handoff);
    if (handoff.localityDetails) controls.localityDetails.setValue(handoff.localityDetails);
    if (handoff.location.pincode) controls.zipCode.setValue(handoff.location.pincode);
    if (handoff.buildingDetails) controls.buildingDetails.setValue(handoff.buildingDetails);
    if (handoff.landmark) controls.landmark.setValue(handoff.landmark);
    this.autofilledValues = { localityDetails: controls.localityDetails.value, zipCode: controls.zipCode.value };
    this.addressFormComponent.setRequiredFieldErrorsVisible(true);
    if (this.fromReviewOrderPage && handoff.location.pincode) this.fetchDeliveryDate(String(handoff.location.pincode));
  }

  private async openRemainingDetailsSheet(focus: AutofillFocus): Promise<void> {
    const data: RemainingDetailsSheetData = {
      form: this.addressFormComponent.addressForm,
      fieldErrors: this.addressFormComponent.addressFieldError,
      focus,
    };
    const sheetRef = this.bottomSheet.open(RemainingDetailsSheetComponent, { ...AUTOFILL_SHEET_CONFIG, data });
    this.currentComponentService.set(sheetRef.instance);
    const result = await firstValueFrom(sheetRef.afterDismissed());
    this.currentComponentService.remove(sheetRef.instance);
    this.currentComponentService.set(this);
    if (result !== RemainingDetailsSheetResult.Save) {
      this.addressFormComponent.setRequiredFieldErrorsVisible(true);
      return;
    }
    this.logAutofillEvent('remaining_details_saved');
    await this.saveAddress();
  }

  private showFillRemainingDetails(focus: AutofillFocus): void {
    this.addressFormComponent.setRequiredFieldErrorsVisible(true);
    const target = focus === AutofillFocus.Area ? this.addressFormComponent.localityDetails : this.addressFormComponent.buildingDetails;
    target?.nativeElement?.focus();
  }

  private notifyDetectFailure(reason: DetectFailure): void {
    this.eventLogger.cleverTapEvent('location_detect_failed', JSON.stringify({ ...this.autofillEventContext(), reason }));
    const message = reason === DetectFailure.PermissionDenied ? LOCATION_PERMISSION_DENIED : NOT_ABLE_TO_DETECT_LOCATION;
    this.broadcast.broadcast('NOTIFY', { message });
  }

  private logAutofillEvent(name: string): void {
    this.eventLogger.cleverTapEvent(name, JSON.stringify(this.autofillEventContext()));
  }

  private autofillEventContext(): Record<string, unknown> {
    return {
      addressPageLocationFlow: this.addressPageLocationFlow,
      reviewOrderCta: this.showLocationCtaOnReviewOrder,
      entry: ADDRESS_PAGE_ENTRY,
    };
  }

  async handleBackPress(): Promise<boolean> {
    if (this.isHandlingShiprocketFlowEditSave) {
      return true;
    }

    if (this.route.snapshot.queryParams.fromCheckout) {
      this.currentComponentService.remove(this);
      const step = this.fromReviewOrderPage ? 1 : 2;
      this.broadcast.broadcast('NAVIGATION_BACK', { step });
      return true;
    }
    return false;
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((each: any): void => each.unsubscribe());
    this.currentComponentService.remove(this);
    if (this.bannerObserver) {
      this.bannerObserver.disconnect();
      this.bannerObserver = null;
    }
  }

  private async initShiprocket(): Promise<void> {
    await this.initShiprocketPrefillIfNeeded();
    this.updateShiprocketBannerVisibility();
    this.subscriptions.push(
      this.broadcast.on<boolean>('KEYBOARD_VISIBLE').subscribe((isVisible: boolean): void => {
        this.isKeyboardVisible = isVisible;
        this.updateShiprocketBannerVisibility();
      })
    );
  }

  private async initShiprocketPrefillIfNeeded(): Promise<void> {
    if (!this.shouldInitShiprocketPrefill()) {
      return;
    }
    // Users who already have saved addresses don't need the Shiprocket prefill banner.
    if (await this.userHasSavedAddresses()) {
      return;
    }
    this.loadShiprocketDataFromStorage();
    if (this.shiprocketAddressesCount > 0) {
      this.shiprocketAddressExists = true;
      return;
    }
    await this.checkShipRocketAddressExists();
  }

  private async userHasSavedAddresses(): Promise<boolean> {
    let addressBookList: AddressBookI[] = [];
    this.store.select(fromSelectors.selectAddressBookList).pipe(take(1))
      .subscribe((list: AddressBookI[]): void => { addressBookList = list || []; });
    if (addressBookList.length) return true;
    try {
      const addresses = await this.conn.fetchAllAddress(['objectId']);
      return !!addresses?.length;
    } catch (error) {
      return false;
    }
  }

  private shouldInitShiprocketPrefill(): boolean {
    if (this.fromReviewOrderPage) {
      return !!this.experimentService.reviewOrderPageExperiment?.variant?.shouldShowShiprocket
        && this.shiprocketService.isAcquisitionUser();
    }
    return this.prefillAddressShiprocketExperiment && this.shiprocketService.isAcquisitionUser();
  }

  private loadShiprocketDataFromStorage(): void {
    const queryParams = this.route.snapshot.queryParams || {};
    const phoneFromQuery = queryParams[this.appConfig.Shared.Shiprocket.PhoneParam];
    const storedData = this.shiprocketService.getAddressData(phoneFromQuery);
    if (storedData) {
      this.shiprocketPhone = storedData.phone;
      this.shiprocketAddressesCount = storedData.addressCount;
    }
  }

  private updateShiprocketBannerVisibility(): void {
    // When keyboard is visible, hide both banners
    if (this.isKeyboardVisible) {
      this.shouldShowShiprocketBannerNonSticky = false;
      this.shouldShowShiprocketBannerSticky = false;
      return;
    }

    // When keyboard is not visible, show banner based on sticky setting
    const effectiveSticky = this.shiprocketSticky;
    this.shouldShowShiprocketBannerNonSticky = this.shiprocketAddressExists && !this.isEdit && !effectiveSticky && this.showShiprocketBanner;
    this.shouldShowShiprocketBannerSticky = this.shiprocketAddressExists && !this.isEdit && effectiveSticky && this.showShiprocketBanner;

    // Set up observer when banner becomes visible
    if ((this.shouldShowShiprocketBannerNonSticky || this.shouldShowShiprocketBannerSticky) && !this.hasSentBannerImpression) {
      setTimeout(() => {
        this.setupBannerIntersectionObserver();
      }, 100);
    }
  }

  private setupBannerIntersectionObserver(): void {
    // Don't set up if already sent impression
    if (this.hasSentBannerImpression) {
      return;
    }

    // Clean up existing observer if any
    if (this.bannerObserver) {
      this.bannerObserver.disconnect();
      this.bannerObserver = null;
    }

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5, // Trigger when 50% of banner is visible
    };

    this.bannerObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]): void => {
      entries.forEach((entry: IntersectionObserverEntry): void => {
        if (entry.isIntersecting && !this.hasSentBannerImpression) {
          this.sendShiprocketBannerImpressionEvent();
          this.hasSentBannerImpression = true;
          // Disconnect after first impression
          if (this.bannerObserver) {
            this.bannerObserver.disconnect();
            this.bannerObserver = null;
          }
        }
      });
    }, options);

    // Observe the visible banner element
    const bannerElement = this.shouldShowShiprocketBannerNonSticky
      ? this.shiprocketBannerNonSticky?.nativeElement
      : this.shouldShowShiprocketBannerSticky
        ? this.shiprocketBannerSticky?.nativeElement
        : null;

    if (bannerElement) {
      this.bannerObserver.observe(bannerElement);
    }
  }

  private sendShiprocketBannerImpressionEvent(): void {
    const bannerType = this.shouldShowShiprocketBannerSticky ? 'sticky' : 'non-sticky';
    const eventData = {
      name: 'shiprocket_address_banner',
      bannerType,
      shiprocketAddressesCount: this.shiprocketAddressesCount,
      isEdit: this.isEdit,
    };
    this.eventLogger.cleverTapEvent('impression', JSON.stringify(eventData));
  }

  private getShiprocketPhone(): string {
    const storedData = this.shiprocketService.getAddressData();
    return storedData?.phone || this.shiprocketPhone || this.user?.get('MobileNumber');
  }

  private navigateToShiprocketAddressList(phone: string): void {
    this.checkoutService.navigateToShiprocketAddressList(
      phone,
      this.route.snapshot.queryParams
    );
  }

  private subscribeToZipCodeChanges(): void {
    if (!this.addressFormComponent?.addressForm?.controls?.zipCode) return;
    this.subscriptions.push(
      this.addressFormComponent.addressForm.controls.zipCode.valueChanges.subscribe((zipCode: number): void => {
        const pincode = String(zipCode || '').trim();
        if (pincode.length === PINCODE_LENGTH) {
          this.fetchDeliveryDate(pincode);
        }
      })
    );
  }

  private async fetchDeliveryDate(pincode: string): Promise<void> {
    try {
      const result = await this.checkoutService.fetchDeliveryDateInfo(
        pincode,
        this.user?.get('languagePreference'),
      );
      this.expectedDeliveryDate = result.expectedDeliveryDate;
      this.dayDifference = result.dayDifference;
    } catch {
      this.expectedDeliveryDate = {};
      this.dayDifference = undefined;
    }
  }

  private initDeliveryDateEstimation(): void {
    const initialPincode = String(this.addressForm?.zipCode || '').trim();
    if (initialPincode.length === PINCODE_LENGTH) {
      this.fetchDeliveryDate(initialPincode);
    } else {
      const lastPincode = this.user?.get('lastLocationPincode');
      if (lastPincode) {
        this.fetchDeliveryDate(String(lastPincode));
      }
    }
  }

  get showRebrandedUI(): boolean {
    return true;
  }

  get isfromReviewOrderAndDeliveryDateExists(): boolean {
    const zipCode = `${this.addressFormComponent?.addressForm?.controls?.zipCode?.value ?? ''}`.trim();
    const hasInvalidPincode = zipCode.length === PINCODE_LENGTH && !!this.addressFormComponent?.pinCodeError;
    return this.fromReviewOrderPage
      && !hasInvalidPincode
      && !!this.expectedDeliveryDate?.from
      && !!this.expectedDeliveryDate?.to;
  }

  get showAddressDisclaimer(): boolean {
    return !this.prefillAddressShiprocketExperiment || this.fromReviewOrderPage;
  }
}
